/**
 * 端末ID⇄編成番号の紐づけ一覧ページ (SBI-5)。
 *
 * 現場 WG の WebApp 上で、DeviceRegistry テーブルに登録されている
 * 「端末ID (device_id) と編成番号 (vehicle_id) の最新紐づけ + 動作ステータス」を
 * 一覧確認する用途。
 *
 * 表示列:
 *   - 編成番号 (vehicle_id)
 *   - 端末ID (device_id)
 *   - 種別 (Web / Android。device_id プレフィックスからフロント側で導出)
 *   - 最終登録日時 (last_registered_at)
 *   - 現在の列番 (current_trip_id。VehicleTripMapping 由来。在線情報が無い場合は "—")
 *   - ステータス (正常=緑バッジ / 異常=赤バッジ + last_alert_sent_at ツールチップ)
 *   - 状態更新時刻 (status_updated_at)
 *
 * フィルタ:
 *   - 「異常のみ表示」トグル (status === 'abnormal' の行のみ表示)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/broadcastPoints';
import { deviceTypeFromId, DeviceRegistrationsClient } from '../api/deviceRegistrations';
import type { DeviceRegistration } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { formatDateTime } from '../utils/dateTime';

interface DeviceRegistrationListPageProps {
  config: AppConfig;
}

/**
 * 現在の列番が解決できない場合の表示。
 *
 * サーバ側 (`GET /device-registrations`) が `current_trip_id: null` を返すのは
 * 「当該編成が在線していない (直近 5 分以内に在線情報が更新されていない)」場合、
 * または VehicleTripMapping の参照に失敗した場合 (フェイルセーフ)。
 */
const CURRENT_TRIP_EMPTY = '—';

/** `current_trip_id` が null のときにツールチップで表示する理由。 */
const CURRENT_TRIP_EMPTY_REASON = '在線情報がありません';

export function DeviceRegistrationListPage(
  props: DeviceRegistrationListPageProps,
): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new DeviceRegistrationsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [items, setItems] = useState<DeviceRegistration[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [abnormalOnly, setAbnormalOnly] = useState(false);
  const requestIdRef = useRef(0);

  /**
   * DeviceRegistry の一覧を取得して state に反映する。
   *
   * `requestIdRef` を用いて古いリクエスト / アンマウント後の結果を破棄し、
   * 競合状態 (race condition) を防ぐ。
   */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      // 古いリクエストまたはアンマウント後は無視
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setItems(res.items);
      setTotalCount(res.count);
    } catch (err) {
      // 古いリクエストまたはアンマウント後は無視
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setErrorMessage(formatApiError(err));
    } finally {
      // アンマウント後や古いリクエストは無視
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  // 初回マウント時 (および apiClient の再生成時) に一覧を取得する。
  // React 18 StrictMode 開発環境では effect が 2 回実行されるが、
  // `requestIdRef` による競合防止で最新の結果のみが反映される。
  // cleanup で requestId をインクリメントすることで、進行中の古いリクエスト結果を
  // 全て無視させ、アンマウント後の setState も防ぐ。
  // reload は先頭で setLoading(true) 等の setState を同期実行するため
  // react-hooks/set-state-in-effect が警告するが、これは外部システム (API) から
  // データを取り込む正当なユースケースであり意図的なものなので抑制する。
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- API 取得の初回ロードは正当な副作用
    void reload();

    // アンマウント時に requestId をインクリメントして、
    // 進行中のリクエスト結果を全て無視させ、二重実行の影響も防ぐ。
    // ここでは ref の「現在値を読む」のではなく単にインクリメントするだけなので、
    // exhaustive-deps の「cleanup 時に ref が変わっている可能性」警告は該当しない。
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref はカウンタ用途で、cleanup での加算は意図的
      ++requestIdRef.current;
    };
  }, [reload]);

  const visibleItems = useMemo(
    () => (abnormalOnly ? items.filter((i) => i.status === 'abnormal') : items),
    [items, abnormalOnly],
  );

  return (
    <section className="section" data-testid="device-list-section">
      <h2>
        端末登録一覧{' '}
        {loading
          ? '(読み込み中...)'
          : visibleItems.length === items.length
            ? `(${totalCount} 件)`
            : `(${visibleItems.length} / ${totalCount} 件)`}
      </h2>

      {errorMessage ? <div className="error-message">{errorMessage}</div> : null}

      <div className="sort-filter-bar">
        <label className="sort-filter-radio">
          <input
            type="checkbox"
            checked={abnormalOnly}
            onChange={(e) => setAbnormalOnly(e.target.checked)}
            data-testid="abnormal-only-toggle"
          />
          異常のみ表示
        </label>
        <button type="button" onClick={() => void reload()} data-testid="device-reload-button">
          再読み込み
        </button>
      </div>

      <div className="table-wrapper">
        <table className="data-table" data-testid="device-table">
          <thead>
            <tr>
              <th scope="col">編成番号</th>
              <th scope="col">端末ID</th>
              <th scope="col">種別</th>
              <th scope="col">最終登録日時</th>
              <th scope="col">現在の列番</th>
              <th scope="col">ステータス</th>
              <th scope="col">状態更新時刻</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 ? (
              <tr>
                <td colSpan={7} className="data-table__empty">
                  {loading ? '読み込み中...' : '該当する端末がありません'}
                </td>
              </tr>
            ) : (
              visibleItems.map((item) => (
                <tr key={item.vehicle_id} data-testid={`device-row-${item.vehicle_id}`}>
                  <td>{item.vehicle_id}</td>
                  <td>{item.device_id}</td>
                  <td>{deviceTypeFromId(item.device_id)}</td>
                  <td>{formatDateTime(item.last_registered_at)}</td>
                  <td>
                    {item.current_trip_id ? (
                      <span data-testid="current-trip-id">{item.current_trip_id}</span>
                    ) : (
                      <span title={CURRENT_TRIP_EMPTY_REASON} data-testid="current-trip-empty">
                        {CURRENT_TRIP_EMPTY}
                        {/*
                          記号 "—" だけでは読み上げで理由が伝わらないため、
                          スクリーンリーダー専用テキストで補う (視覚上は非表示)。
                          マウス利用者には title 属性のツールチップで同じ情報を提供する。
                        */}
                        <span className="visually-hidden">（{CURRENT_TRIP_EMPTY_REASON}）</span>
                      </span>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={item.status} lastAlertSentAt={item.last_alert_sent_at} />
                  </td>
                  <td>{formatDateTime(item.status_updated_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface StatusBadgeProps {
  status: DeviceRegistration['status'];
  lastAlertSentAt: string | null;
}

/**
 * ステータスバッジ。
 * - `normal`: 緑バッジ「正常」
 * - `abnormal`: 赤バッジ「異常」+ last_alert_sent_at をツールチップ (title) 表示
 * - その他 / null: 中立バッジ「不明」
 */
function StatusBadge({ status, lastAlertSentAt }: StatusBadgeProps): React.JSX.Element {
  if (status === 'normal') {
    return (
      <span
        className="status-badge status-badge--normal"
        aria-label="ステータス: 正常"
        data-testid="status-badge"
      >
        正常
      </span>
    );
  }
  if (status === 'abnormal') {
    const tooltip = lastAlertSentAt
      ? `最終アラート送信: ${formatDateTime(lastAlertSentAt)}`
      : 'アラート送信履歴なし';
    return (
      <span
        className="status-badge status-badge--abnormal"
        title={tooltip}
        aria-label={`ステータス: 異常。${tooltip}`}
        tabIndex={0}
        data-testid="status-badge"
      >
        異常
      </span>
    );
  }
  return (
    <span
      className="status-badge status-badge--unknown"
      aria-label="ステータス: 不明"
      data-testid="status-badge"
    >
      不明
    </span>
  );
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return `[HTTP ${err.status}] ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return '予期しないエラーが発生しました';
}
